import { ChangeDetectionStrategy, Component, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzFormModule } from 'ng-zorro-antd/form';
import { OPERATOR_REASONS, OperatorReason } from '../../../model/operator-reason';

/**
 * Standalone modal that prompts an Operator to select a reason before committing
 * a manual status change. Mirrors the dlg-bad-bucket-position standalone pattern.
 *
 * Usage:
 *   <app-operator-reason-dialog
 *     [(nzVisible)]="showReasonDialog"
 *     (reasonSelected)="onReasonSelected($event)"
 *     (cancelled)="onReasonCancelled()"
 *   />
 *
 * - `reasonSelected` emits the chosen reason key (ALL_CAPS) — caller commits the status change.
 * - `cancelled`      emits void                              — caller aborts the status change.
 */
@Component({
  selector: 'app-operator-reason-dialog',
  templateUrl: './operator-reason-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    NzButtonModule,
    NzModalModule,
    NzSelectModule,
    NzFormModule,
  ],
})
export class OperatorReasonDialogComponent {
  /** Emits the chosen reason key when the user clicks OK. */
  readonly reasonSelected = output<string>();

  /** Emits void when the user clicks Cancel — the caller must abort the status change. */
  readonly cancelled = output<void>();

  /** Whether the modal is currently visible; managed externally via nzVisible two-way binding. */
  readonly visible = signal(false);

  /** The currently selected reason key (null = nothing selected yet). */
  protected readonly selectedKey = signal<string | null>(null);

  /** The full reason list passed to the select. */
  protected readonly reasons: OperatorReason[] = OPERATOR_REASONS;

  /** Opens the dialog and resets any previous selection. */
  open(): void {
    this.selectedKey.set(null);
    this.visible.set(true);
  }

  /** Handles OK button: emits the selected key and closes. No-op if nothing selected. */
  protected onOk(): void {
    const key = this.selectedKey();
    if (!key) return;
    this.visible.set(false);
    this.reasonSelected.emit(key);
  }

  /** Handles Cancel button: closes and emits cancelled so the caller can abort. */
  protected onCancel(): void {
    this.visible.set(false);
    this.cancelled.emit();
  }
}
